import { Menu, Popover, type PopoverOrigin } from "@mui/material";
import { type RefObject } from "react";

import { BREAKPOINT } from "@/configs/breakpoints";
import { useMediaQuery } from "@/hooks/use-media-query";

import { useEdgeProximity } from "@/hooks/use-edge-proximity";
import { getCategoryTitle, groupBy, sortGroupItems } from "./constants";
import { GroupMenuContents } from "./group-menu-contents";
import { type GroupItem, type SharedGroupMenuProps } from "./types";

interface GroupMenuProps extends SharedGroupMenuProps {
    items: GroupItem[];
    anchorRef: RefObject<HTMLElement | null>;
}
/**
 * Group Menu displays a list of the devices/icons present in a group
 */
export function GroupMenu({ items, ...props }: GroupMenuProps) {
    const sortedItems = sortGroupItems(items);
    const categorizedItems = groupBy(sortedItems, (item: GroupItem) => getCategoryTitle(item.zones ?? []));
    const isSmallScreen = useMediaQuery(BREAKPOINT.DEVICE_CARD_FULL_WIDTH);

    return isSmallScreen ? (
        <MobileGroupMenu categorizedItems={categorizedItems} {...props} />
    ) : (
        <DesktopGroupMenu categorizedItems={categorizedItems} {...props} />
    );
}

// Threshold for the desktop group menu to be considered near the browser edge, in pixels
// Related to the max width of the menu, but sort of an arbitrary number
const DESKTOP_GROUP_MENU_BROWSER_EDGE_THRESHOLD = 450;

// Options for the position of the desktop group menu
enum DESKTOP_GROUP_MENU_POSITION {
    LEFT = "left",
    RIGHT = "right",
}

// Gap between menu and anchor element
const MENU_SPACING_PX = 8;

const DESKTOP_GROUP_MENU_POSITION_CONFIG: Record<
    DESKTOP_GROUP_MENU_POSITION,
    { origin: PopoverOrigin; transformOrigin: PopoverOrigin }
> = {
    [DESKTOP_GROUP_MENU_POSITION.LEFT]: {
        origin: {
            vertical: "center",
            horizontal: "left",
        },
        transformOrigin: {
            vertical: 20,
            horizontal: "right", // Align right edge of menu with anchor, so the menu is on the left side of the anchor
        },
    },
    [DESKTOP_GROUP_MENU_POSITION.RIGHT]: {
        origin: {
            vertical: "center",
            horizontal: "right",
        },
        transformOrigin: {
            vertical: 20,
            horizontal: "left", // Align left edge of menu with anchor, so the menu is on the right side of the anchor
        },
    },
};

interface DesktopGroupMenuProps extends Omit<GroupMenuProps, "items"> {
    categorizedItems: Record<string, GroupItem[]>;
}
/**
 * Appears as menu anchored to the side of an icon
 */
function DesktopGroupMenu({ categorizedItems, open, onClose, anchorRef, groupMenuOptions }: DesktopGroupMenuProps) {
    const { menuMaxHeight, menuPadding } = groupMenuOptions;

    const { nearWindowEdges } = useEdgeProximity({
        ref: anchorRef,
        threshold: DESKTOP_GROUP_MENU_BROWSER_EDGE_THRESHOLD,
    });

    // Determine the alignment of the menu based on the position of the anchor element
    const alignment = nearWindowEdges?.right ? DESKTOP_GROUP_MENU_POSITION.LEFT : DESKTOP_GROUP_MENU_POSITION.RIGHT;

    return (
        <Menu
            sx={{
                "& .MuiMenu-paper": {
                    maxHeight: `${menuMaxHeight}px`,
                    backgroundColor: "white",
                    border: "1px solid #F2F0F1",
                    overflowY: "auto",
                    padding: `${menuPadding}px`,
                    // Translate the menu to add padding between the menu and the anchor element
                    transform:
                        alignment === DESKTOP_GROUP_MENU_POSITION.LEFT
                            ? `translateX(-${MENU_SPACING_PX}px) !important`
                            : `translateX(${MENU_SPACING_PX}px) !important`,
                },
                "& .MuiMenu-list": {
                    py: 0,
                },
            }}
            elevation={0}
            open={open}
            onClose={onClose}
            anchorEl={anchorRef.current}
            anchorOrigin={{
                vertical: DESKTOP_GROUP_MENU_POSITION_CONFIG[alignment].origin.vertical,
                horizontal: DESKTOP_GROUP_MENU_POSITION_CONFIG[alignment].origin.horizontal,
            }}
            transformOrigin={{
                vertical: DESKTOP_GROUP_MENU_POSITION_CONFIG[alignment].transformOrigin.vertical,
                horizontal: DESKTOP_GROUP_MENU_POSITION_CONFIG[alignment].transformOrigin.horizontal,
            }}
        >
            <GroupMenuContents
                open={open}
                categorizedItems={categorizedItems}
                onClose={onClose}
                groupMenuOptions={groupMenuOptions}
            />
        </Menu>
    );
}

interface MobileGroupMenuProps extends SharedGroupMenuProps {
    categorizedItems: Record<string, GroupItem[]>;
}
/**
 * Appears as a card-like popup, appearing from the bottom of the screen
 */
function MobileGroupMenu({ categorizedItems, open, onClose, groupMenuOptions }: MobileGroupMenuProps) {
    const { menuMaxHeight, menuPadding } = groupMenuOptions;

    return (
        <Popover
            sx={{
                "& .MuiPaper-root": {
                    position: "absolute",
                    left: "15px",
                    right: "15px",
                    bottom: "15px",
                    margin: "auto",
                    maxHeight: `${menuMaxHeight}px`,
                    border: "1px solid #sF2F0F1",
                    overflowY: "auto",
                    padding: `${menuPadding}px`,
                },
            }}
            elevation={0}
            open={open}
            onClose={onClose}
            anchorReference={"none"}
        >
            <GroupMenuContents
                open={open}
                categorizedItems={categorizedItems}
                onClose={onClose}
                groupMenuOptions={groupMenuOptions}
            />
        </Popover>
    );
}
